import { useRef, useCallback } from "react";
import html2canvas from "html2canvas";
import { Button } from "@/components/ui/button";
import { Download, Link2, MessageSquare, CheckCircle2, Clock, Mail } from "lucide-react";
import { toast } from "sonner";
import { useBranding } from "@/hooks/useBranding";

interface SequenceStep {
  step_number: number;
  action_type: string;
  delay_hours: number;
  message_template?: string | null;
  include_message?: boolean;
  use_inmail?: boolean;
}

interface SequenceExportCardProps {
  sequences: SequenceStep[];
  campaignName: string;
  clientEmail?: string;
}

const getActionIcon = (actionType: string) => {
  switch (actionType) {
    case "connect": return <Link2 className="w-5 h-5" />;
    case "message": return <MessageSquare className="w-5 h-5" />;
    case "inmail": return <Mail className="w-5 h-5" />;
    case "end": return <CheckCircle2 className="w-5 h-5" />;
    default: return <MessageSquare className="w-5 h-5" />;
  }
};

const getActionLabel = (actionType: string) => {
  switch (actionType) {
    case "connect": return "Connection Request";
    case "message": return "Follow-up Message";
    case "inmail": return "InMail Message";
    case "end": return "Sequence Complete";
    default: return actionType;
  }
};

const getActionColor = (actionType: string) => {
  switch (actionType) {
    case "connect": return { bg: "#EEF2FF", border: "#818CF8", text: "#4338CA", iconBg: "#C7D2FE" };
    case "message": return { bg: "#F0FDF4", border: "#4ADE80", text: "#166534", iconBg: "#BBF7D0" };
    case "inmail": return { bg: "#FFF7ED", border: "#FB923C", text: "#9A3412", iconBg: "#FED7AA" };
    case "end": return { bg: "#F8FAFC", border: "#94A3B8", text: "#475569", iconBg: "#E2E8F0" };
    default: return { bg: "#F8FAFC", border: "#94A3B8", text: "#475569", iconBg: "#E2E8F0" };
  }
};

const formatDelay = (hours: number) => {
  if (hours < 1) return "Immediately";
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""}`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  if (remainingHours === 0) return `${days} day${days > 1 ? "s" : ""}`;
  return `${days}d ${remainingHours}h`;
};

export const SequenceExportCard = ({ sequences, campaignName, clientEmail }: SequenceExportCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const brand = useBranding();

  const activeSteps = sequences.filter(s => s.action_type !== "end");
  const stepCount = activeSteps.length;

  const handleExport = useCallback(async () => {
    if (!cardRef.current) return;

    try {
      // Temporarily show the card for capture
      const el = cardRef.current;
      el.style.position = "fixed";
      el.style.left = "0";
      el.style.top = "0";
      el.style.zIndex = "-9999";
      el.style.opacity = "1";
      el.style.pointerEvents = "none";
      el.style.display = "block";

      const canvas = await html2canvas(el, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
        logging: false,
      });

      // Hide again
      el.style.display = "none";

      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${campaignName.replace(/[^a-zA-Z0-9]/g, "_")}_sequence.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Sequence image downloaded!");
      }, "image/png");
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Failed to export sequence image");
    }
  }, [campaignName]);

  return (
    <>
      <Button variant="outline" size="sm" onClick={handleExport} className="gap-2">
        <Download className="w-4 h-4" />
        Export Image
      </Button>

      {/* Hidden render target for html2canvas */}
      <div ref={cardRef} style={{ display: "none", width: 900 }}>
        <div style={{
          width: 900,
          fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
          background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)",
          borderRadius: 20,
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.1)",
          border: "1px solid #E2E8F0",
        }}>
          {/* Header */}
          <div style={{
            background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 50%, #6366F1 100%)",
            padding: "36px 40px 32px",
            position: "relative",
            overflow: "hidden",
          }}>
            {/* Decorative circles */}
            <div style={{
              position: "absolute", right: -30, top: -30,
              width: 120, height: 120, borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
            }} />
            <div style={{
              position: "absolute", right: 40, bottom: -20,
              width: 80, height: 80, borderRadius: "50%",
              background: "rgba(255,255,255,0.05)",
            }} />

            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: "rgba(255,255,255,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
                backdropFilter: "blur(10px)",
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                  <rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/>
                </svg>
              </div>
              <span style={{
                color: "rgba(255,255,255,0.9)",
                fontSize: 13, fontWeight: 600,
                letterSpacing: "0.1em", textTransform: "uppercase",
              }}>
                Campaign Outreach Sequence
              </span>
            </div>

            <h1 style={{
              color: "#FFFFFF", fontSize: 26, fontWeight: 700,
              margin: 0, lineHeight: 1.3,
            }}>
              {campaignName}
            </h1>
            {clientEmail && (
              <p style={{
                color: "rgba(255,255,255,0.7)", fontSize: 14,
                margin: "8px 0 0", fontWeight: 400,
              }}>
                {clientEmail}
              </p>
            )}
          </div>

          {/* Steps */}
          <div style={{ padding: "32px 40px 24px" }}>
            {sequences.map((step, index) => {
              const colors = getActionColor(step.action_type);
              const isLast = index === sequences.length - 1;
              const showDelay = !isLast && sequences[index + 1]?.delay_hours > 0;

              return (
                <div key={step.step_number} style={{ position: "relative" }}>
                  {/* Step Card */}
                  <div style={{
                    background: colors.bg,
                    border: `1.5px solid ${colors.border}40`,
                    borderRadius: 14,
                    padding: "20px 24px",
                    position: "relative",
                  }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                      {/* Icon */}
                      <div style={{
                        width: 44, height: 44, borderRadius: 12,
                        background: colors.iconBg,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, color: colors.text,
                      }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={colors.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          {step.action_type === "connect" && (
                            <>
                              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                            </>
                          )}
                          {step.action_type === "message" && (
                            <>
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                            </>
                          )}
                          {step.action_type === "inmail" && (
                            <>
                              <rect width="20" height="16" x="2" y="4" rx="2"/>
                              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                            </>
                          )}
                          {step.action_type === "end" && (
                            <>
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                              <polyline points="22 4 12 14.01 9 11.01"/>
                            </>
                          )}
                        </svg>
                      </div>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: step.message_template ? 10 : 0 }}>
                          <span style={{
                            fontSize: 11, fontWeight: 700, color: colors.text,
                            background: `${colors.border}30`, borderRadius: 6,
                            padding: "2px 8px", textTransform: "uppercase",
                            letterSpacing: "0.05em",
                          }}>
                            Step {step.step_number}
                          </span>
                          <span style={{
                            fontSize: 16, fontWeight: 600, color: "#1E293B",
                          }}>
                            {getActionLabel(step.action_type)}
                          </span>
                          {step.use_inmail && (
                            <span style={{
                              fontSize: 11, fontWeight: 600, color: "#9A3412",
                              background: "#FED7AA", borderRadius: 6,
                              padding: "2px 8px",
                            }}>
                              InMail
                            </span>
                          )}
                        </div>

                        {step.message_template && (
                          <div style={{
                            background: "rgba(255,255,255,0.7)",
                            border: "1px solid rgba(0,0,0,0.06)",
                            borderRadius: 10, padding: "14px 16px",
                            fontSize: 14, lineHeight: 1.7,
                            color: "#475569",
                            whiteSpace: "pre-wrap",
                          }}>
                            {step.message_template}
                          </div>
                        )}

                        {step.action_type === "connect" && step.include_message && !step.message_template && (
                          <div style={{
                            fontSize: 12, color: "#6366F1",
                            display: "flex", alignItems: "center", gap: 4, marginTop: 4,
                          }}>
                            📎 Connection note included
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Connector Line + Delay Badge */}
                  {!isLast && (
                    <div style={{
                      display: "flex", flexDirection: "column",
                      alignItems: "center", padding: "4px 0",
                      position: "relative",
                    }}>
                      <div style={{
                        width: 2, height: showDelay ? 12 : 20,
                        background: "linear-gradient(180deg, #CBD5E1, #94A3B8)",
                      }} />
                      {showDelay && (
                        <>
                          <div style={{
                            display: "flex", alignItems: "center", gap: 6,
                            background: "#FFFFFF",
                            border: "1.5px solid #E2E8F0",
                            borderRadius: 20, padding: "5px 14px",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                          }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/>
                              <polyline points="12 6 12 12 16 14"/>
                            </svg>
                            <span style={{
                              fontSize: 12, fontWeight: 600, color: "#64748B",
                            }}>
                              Wait {formatDelay(sequences[index + 1].delay_hours)}
                            </span>
                          </div>
                          <div style={{
                            width: 2, height: 12,
                            background: "linear-gradient(180deg, #94A3B8, #CBD5E1)",
                          }} />
                        </>
                      )}
                      {!showDelay && null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{
            borderTop: "1px solid #E2E8F0",
            padding: "20px 40px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#F8FAFC",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 7,
                background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                  <rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/>
                </svg>
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#64748B" }}>
                Powered by {brand.productName}
              </span>
            </div>
            <span style={{
              fontSize: 13, fontWeight: 600, color: "#94A3B8",
            }}>
              {stepCount} Step{stepCount !== 1 ? "s" : ""} • Automated Outreach
            </span>
          </div>
        </div>
      </div>
    </>
  );
};
